import { useState } from "react";
import { hasSpecialCharacters } from "../../Helpers/miscellaneous";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { PRODUCTION_INSTANCE_TYPE } from "../CreateInstanceDialog/CONSTANTS";
import { Checkbox } from "@progress/kendo-react-inputs";
import { useTranslation } from "react-i18next";

/**
 * Function representing an update instance dialog.
 * 
 * @param {Object} props - The properties object.
 * @param {instanceTypes.Instance} props.instance - The instance to be updated.
 * @param {(name: string) => boolean} props.isNameInUse - Function that takes a string parameter (name) and returns a boolean indicating if the name is in use.
 * @param {Function} props.onUpdate - Callback function invoked when an update is performed.
 * @param {Function} props.onCancel - Callback function invoked when the update process is canceled.
 * @returns {JSX.Element} - The JSX element representing the update instance dialog.
 */
/**
 * UpdateInstanceDialog component allows users to update the details of an instance.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Object} props.instance - The instance object containing current details.
 * @param {string} props.instance.instanceName - The name of the instance.
 * @param {string} props.instance.description - The description of the instance.
 * @param {function} props.isNameInUse - Function to check if the instance name is already in use.
 * @param {function} props.onUpdate - Callback function to handle the update action.
 * @param {function} props.onCancel - Callback function to handle the cancel action.
 * @returns {JSX.Element} The rendered component.
 */
function UpdateInstanceDialog({ instance, isNameInUse, onUpdate, onCancel }) {
    const { t } = useTranslation("Instances/Instances");
 
    const [state, setState] = useState({
        instanceName: instance.instanceName,
        description: instance.description
    });
    const [errors, setErrors] = useState({ instanceName: "" });

    const handleChange = (e) => {
        const { name, value, checked, type } = e.target;
        const newValue = type === "checkbox" ? checked : value;
        const newErrors = handleErrors(name, newValue);

        setState((prevState) => ({ ...prevState, [name]: newValue }));
        setErrors(newErrors);
    };

    const handleCancel = () => {
        onCancel();
    };

    const handleErrors = (name, value) => {
        const newErrors = { instanceName: "" };

        if (name === "instanceName") {
            if (value.trim().length === 0) {
                newErrors.instanceName = t("form.errors.name_required");
            } else if (value.trim().toLowerCase() !== instance.instanceName.trim().toLowerCase() && isNameInUse(value)) {
                newErrors.instanceName = t("form.errors.name_in_use");
            } else if (hasSpecialCharacters(value)) {
                newErrors.instanceName = t("form.errors.name_cannot_contain_special_characters");
            }
        }

        return newErrors;
    };

    const isFormChanged = () => {
        return (
            state.instanceName.trim() !== instance.instanceName.trim() ||
            state.description.trim() !== instance.description.trim()
        );
    };

    const shouldDisableUpdateButton = () => {
        return (
            state.instanceName.trim().length === 0 ||
            errors.instanceName.trim().length > 0 ||
            !isFormChanged()
        );
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        onUpdate({ ...state });
    };

    return (
        <Dialog title={t("form.update_title")} onClose={handleCancel}>
            <div className="container">
                <div className="row">
                    <div className="col-12 form-group">
                        <label className="form-label" htmlFor="instance_nameField" required>
                            {t("form.name")}
                            {errors.instanceName && (
                                <span className="text-danger"> {errors.instanceName}</span>
                            )}
                        </label>
                        <input
                            id="instance_nameField"
                            name="instanceName"
                            className="form-control"
                            value={state.instanceName}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="col-12 form-group">
                        <label className="form-label" htmlFor="instance_descriptionField">
                            {t("form.description")}
                        </label>
                        <textarea
                            id="instance_descriptionField"
                            name="description"
                            className="form-control"
                            value={state.description}
                            onChange={handleChange}
                        />
                    </div>
                </div>
            </div>
            <DialogActionsBar layout="end">
                <div className="mb-3 me-3">
                    <button className="btn btn-secondary" onClick={handleCancel}>
                        {t("form.buttons.cancel")}
                    </button>
                    <button
                        className="btn btn-primary"
                        disabled={shouldDisableUpdateButton()}
                        onClick={handleSubmit}
                    >
                        {t("form.buttons.update")}
                    </button>
                </div>
            </DialogActionsBar>
        </Dialog>
    );
}

UpdateInstanceDialog.defaultProps = {
    instance: {
        instanceName: "",
        description: "",
        instanceType: PRODUCTION_INSTANCE_TYPE
    },
    isNameInUse: () => false,
    onUpdate: () => {},
    onCancel: () => {}
};

export default UpdateInstanceDialog;
